import { useCallback, useEffect, useRef, useState } from "react";

import {
	Phone,
	Delete,
	PhoneIncoming,
	PhoneOutgoing,
	PhoneMissed,
	Loader2,
	ChevronDown,
} from "lucide-react";

import { cn } from "../../lib/utils";
import api from "../../services/api";
import { definirNumero } from "../../ligacoes/store";
import { Avatar, formatarNumero, nomeDe } from "./pecas";

const TECLAS = [
	["1", ""],
	["2", "ABC"],
	["3", "DEF"],
	["4", "GHI"],
	["5", "JKL"],
	["6", "MNO"],
	["7", "PQRS"],
	["8", "TUV"],
	["9", "WXYZ"],
	["*", ""],
	["0", "+"],
	["#", ""],
];

const quandoFoi = (data) => {
	const d = new Date(data);
	const hoje = new Date();
	const ontem = new Date(Date.now() - 86400000);
	const hora = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
	if (d.toDateString() === hoje.toDateString()) return hora;
	if (d.toDateString() === ontem.toDateString()) return `ontem ${hora}`;
	return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};

const IconeRecente = ({ l }) => {
	if (l.status === "missed") return <PhoneMissed className="h-3.5 w-3.5 text-red-500" />;
	if (l.direction === "outbound") return <PhoneOutgoing className="h-3.5 w-3.5 text-muted-foreground" />;
	return <PhoneIncoming className="h-3.5 w-3.5 text-emerald-500" />;
};

const Discador = ({ numero, conexoes, conexao, setConexao, podeLigar, ocupado, aoLigar, recentes }) => {
	const [aba, setAba] = useState("teclado");
	const [sugestoes, setSugestoes] = useState([]);
	const [escolhido, setEscolhido] = useState(null);
	const inputRef = useRef(null);
	const segurar = useRef(null);

	const digitos = numero.replace(/\D/g, "");
	const temLetra = /[a-zA-ZÀ-ú]/.test(numero);
	const pronto = digitos.length >= 10 && !temLetra && conexao && podeLigar && !ocupado;
	const linhasProntas = conexoes.filter((c) => c.paired);
	const linhaAtual = conexoes.find((c) => String(c.id) === conexao);

	// Nome digitado → busca contato; número → confirma de quem é.
	useEffect(() => {
		setEscolhido(null);
		const termo = numero.trim();
		if ((temLetra && termo.length < 2) || (!temLetra && digitos.length < 4)) {
			setSugestoes([]);
			return undefined;
		}
		const t = setTimeout(async () => {
			try {
				const { data } = await api.get("/contacts", {
					params: { searchParam: temLetra ? termo : digitos, pageNumber: 1 },
				});
				setSugestoes(
					(data.contacts || []).filter((c) => !c.isGroup && String(c.number || "").replace(/\D/g, "").length >= 10).slice(0, 3)
				);
			} catch {
				setSugestoes([]);
			}
		}, 250);
		return () => clearTimeout(t);
	}, [numero, temLetra, digitos]);

	const digitar = useCallback((t) => definirNumero(numero + t), [numero]);

	const apagar = () => definirNumero(numero.slice(0, -1));

	// Segurar: "0" vira "+", apagar limpa tudo — gesto de celular.
	const iniciarSegurar = (acao) => {
		segurar.current = setTimeout(() => {
			segurar.current = "feito";
			acao();
		}, 550);
	};
	const soltar = (acaoCurta) => {
		if (segurar.current === "feito") {
			segurar.current = null;
			return;
		}
		clearTimeout(segurar.current);
		segurar.current = null;
		acaoCurta();
	};

	const exato = sugestoes.find((c) => String(c.number).replace(/\D/g, "") === digitos);

	return (
		<div className="flex flex-col">
			<div className="mx-3 mt-3 grid grid-cols-2 rounded-full bg-muted p-0.5 text-xs font-medium">
				{[
					["teclado", "Teclado"],
					["recentes", "Recentes"],
				].map(([v, r]) => (
					<button
						key={v}
						type="button"
						onClick={() => setAba(v)}
						className={cn(
							"rounded-full py-1.5 transition-colors",
							aba === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
						)}
					>
						{r}
					</button>
				))}
			</div>

			{aba === "recentes" ? (
				<div className="max-h-[360px] min-h-[300px] overflow-y-auto px-2 py-2">
					{recentes.length === 0 ? (
						<p className="px-4 py-16 text-center text-xs text-muted-foreground">
							Nenhuma ligação ainda. As feitas e recebidas aparecem aqui.
						</p>
					) : (
						recentes.map((l) => (
							<button
								key={l.id}
								type="button"
								disabled={!l.number || ocupado || !podeLigar}
								onClick={() => l.number && aoLigar(l.number)}
								className="group flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-muted"
							>
								<Avatar nome={nomeDe(l)} foto={l.contact?.profilePicUrl} tamanho={36} />
								<span className="min-w-0 flex-1">
									<span className={cn("block truncate text-sm", l.status === "missed" && "text-red-500")}>
										{nomeDe(l)}
									</span>
									<span className="flex items-center gap-1 text-[11px] text-muted-foreground">
										<IconeRecente l={l} />
										{quandoFoi(l.startedAt)}
										{l.user?.name ? ` · ${l.user.name}` : ""}
									</span>
								</span>
								<Phone className="h-4 w-4 text-emerald-500 opacity-0 transition-opacity group-hover:opacity-100" />
							</button>
						))
					)}
				</div>
			) : (
				<div className="flex flex-col items-center px-4 pb-4 pt-3">
					{/* visor */}
					<div className="relative w-full">
						<input
							ref={inputRef}
							autoFocus
							inputMode="tel"
							aria-label="Número ou nome do contato"
							placeholder="Número ou nome"
							value={temLetra ? numero : formatarNumero(numero) || numero}
							onChange={(e) => {
								const v = e.target.value;
								definirNumero(/[a-zA-ZÀ-ú]/.test(v) ? v : v.replace(/[^\d+*#]/g, ""));
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter" && pronto) aoLigar(digitos);
							}}
							className="h-12 w-full bg-transparent text-center text-[26px] font-light tracking-wide tabular-nums outline-none placeholder:text-base placeholder:font-normal placeholder:tracking-normal placeholder:text-muted-foreground/60"
						/>
					</div>

					{/* de quem é / sugestões */}
					<div className="flex h-7 w-full items-center justify-center">
						{exato || escolhido ? (
							<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
								<Avatar nome={(exato || escolhido).name} foto={(exato || escolhido).profilePicUrl} tamanho={18} />
								{(exato || escolhido).name}
							</span>
						) : sugestoes.length ? (
							<div className="flex max-w-full gap-1 overflow-x-auto">
								{sugestoes.map((c) => (
									<button
										key={c.id}
										type="button"
										onClick={() => {
											definirNumero(String(c.number).replace(/\D/g, ""));
											setEscolhido(c);
										}}
										className="flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] hover:bg-muted"
									>
										<Avatar nome={c.name} foto={c.profilePicUrl} tamanho={16} />
										<span className="max-w-[90px] truncate">{c.name}</span>
									</button>
								))}
							</div>
						) : temLetra ? (
							<span className="text-[11px] text-muted-foreground">Nenhum contato com esse nome</span>
						) : null}
					</div>

					{/* teclado */}
					<div className="mt-1 grid grid-cols-3 gap-x-5 gap-y-2.5">
						{TECLAS.map(([t, letras]) => (
							<button
								key={t}
								type="button"
								onPointerDown={() => t === "0" && iniciarSegurar(() => digitar("+"))}
								onPointerUp={() => (t === "0" ? soltar(() => digitar("0")) : digitar(t))}
								onPointerLeave={() => t === "0" && segurar.current && segurar.current !== "feito" && clearTimeout(segurar.current)}
								className="flex h-14 w-14 select-none flex-col items-center justify-center rounded-full bg-muted/60 transition-all duration-100 hover:bg-muted active:scale-95 active:bg-muted-foreground/20"
							>
								<span className="text-xl font-light leading-none">{t}</span>
								<span className="mt-0.5 h-2.5 text-[8px] font-semibold tracking-[0.15em] text-muted-foreground">
									{letras}
								</span>
							</button>
						))}
					</div>

					{/* linha de saída */}
					{conexoes.length > 1 ? (
						<label className="relative mt-3 flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] text-muted-foreground hover:bg-muted">
							Sair por
							<b className="font-medium text-foreground">{linhaAtual?.name || "escolha"}</b>
							<ChevronDown className="h-3 w-3" />
							<select
								aria-label="Sair por qual número"
								value={conexao}
								onChange={(e) => setConexao(e.target.value)}
								className="absolute inset-0 cursor-pointer opacity-0"
							>
								{conexoes.map((c) => (
									<option key={c.id} value={String(c.id)} disabled={!c.paired}>
										{c.name}
										{c.number ? ` · ${formatarNumero(c.number)}` : ""}
										{!c.paired ? " (desconectada)" : ""}
									</option>
								))}
							</select>
						</label>
					) : linhaAtual ? (
						<p className="mt-3 text-[11px] text-muted-foreground">
							Sai por <b className="font-medium text-foreground">{linhaAtual.name}</b>
						</p>
					) : null}

					{/* ligar + apagar */}
					<div className="mt-3 grid w-full grid-cols-3 items-center">
						<span />
						<div className="flex justify-center">
							<button
								type="button"
								disabled={!pronto}
								onClick={() => aoLigar(digitos)}
								aria-label="Ligar"
								title={
									!linhasProntas.length
										? "Nenhuma linha conectada"
										: digitos.length < 10
											? "Digite DDI + DDD + número"
											: "Ligar"
								}
								className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 transition-all hover:bg-emerald-600 active:scale-95 disabled:bg-emerald-500/40 disabled:shadow-none"
							>
								{ocupado ? <Loader2 className="h-6 w-6 animate-spin" /> : <Phone className="h-7 w-7" />}
							</button>
						</div>
						<div className="flex justify-center">
							{numero && (
								<button
									type="button"
									aria-label="Apagar (segure para limpar)"
									title="Apagar · segure para limpar"
									onPointerDown={() => iniciarSegurar(() => definirNumero(""))}
									onPointerUp={() => soltar(apagar)}
									className="flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
								>
									<Delete className="h-5 w-5" />
								</button>
							)}
						</div>
					</div>

					{!linhasProntas.length && (
						<p className="mt-3 text-center text-[11px] text-muted-foreground">
							Nenhuma linha conectada. Pareie em Ligações › Aparelhos.
						</p>
					)}
					{!podeLigar && (
						<p className="mt-3 text-center text-[11px] text-muted-foreground">
							Seu cargo não tem permissão para fazer ligações.
						</p>
					)}
				</div>
			)}
		</div>
	);
};

export default Discador;
